import { t, TFunction } from "i18next";
import { z } from "zod";

export const createPasswordSchema = (
  translate: TFunction,
  isPasswordValid: (password: string) => boolean,
) =>
  z.object({
    password: z
      .string()
      .min(6, translate("passwordToShort"))
      .max(50, translate("passwordToLong"))
      .transform((val) => val.trim())
      .refine((val) => isPasswordValid(val), {
        message: translate("recover.invalidPassword"),
      }),
  });

export type PasswordType = z.infer<ReturnType<typeof createPasswordSchema>>;

export type RecoveryPhraseValidator<ValidationParams> = {
  validate: (
    enteredPhrase: string[],
    ctx: z.RefinementCtx,
    params: ValidationParams,
  ) => void;
  validationParams: ValidationParams;
};
export const createRecoveryPhraseSchema = <ValidationParams>(
  validator: RecoveryPhraseValidator<ValidationParams>,
) =>
  z.object({
    phrase: z
      .array(z.string().transform((val) => val.toLowerCase()))
      .superRefine((enteredPhrase, ctx) => {
        let hasEmptyWord = false;
        for (let i = 0; i < enteredPhrase.length; i++) {
          if (enteredPhrase[i].trim().length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: t("recover.wordCannotBeEmpty"),
              path: [i],
            });
            hasEmptyWord = true;
          }
        }

        if (hasEmptyWord) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t("recover.fillAllWords"),
            path: [],
          });
          return;
        }

        validator.validate(enteredPhrase, ctx, validator.validationParams);
      }),
  });

export const validateExactMatch = (
  entered: string[],
  ctx: z.RefinementCtx,
  expected: string[],
) => {
  if (entered.join(" ") !== expected.join(" ")) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: t("recover.confirmPhraseError"),
    });
  }
};

export type RecoveryPhraseType = z.infer<
  ReturnType<typeof createRecoveryPhraseSchema>
>;
